// Comprehensive audit trail service
// Logs all user actions to both IndexedDB (client) and server API

import { logAudit as storageLogAudit } from './storage';

type AuditDetails = Record<string, unknown>;

async function log(action: string, details: AuditDetails = {}, success = true, error?: string): Promise<void> {
  // Write to local IndexedDB
  storageLogAudit({ action, details, success, error }).catch(() => {});
}

// ==================== Authentication / Session ====================

export function auditLogin(provider: string, email?: string): void {
  log('USER_LOGIN', { provider, email });
}

export function auditLogout(): void {
  log('USER_LOGOUT', {});
}

export function auditSessionStart(): void {
  // eslint-disable-next-line n/no-unsupported-features/node-builtins
  log('SESSION_START', { userAgent: navigator.userAgent, timestamp: new Date().toISOString() });
}

// ==================== Navigation ====================

export function auditViewOpened(view: string, details?: AuditDetails): void {
  log('VIEW_OPENED', { view, ...details });
}

export function auditViewClosed(view: string): void {
  log('VIEW_CLOSED', { view });
}

// ==================== Analysis ====================

export function auditAnalysisStarted(mode: string, details?: AuditDetails): void {
  log('ANALYSIS_STARTED', { mode, ...details });
}

export function auditAnalysisCompleted(mode: string, durationMs: number, details?: AuditDetails): void {
  log('ANALYSIS_COMPLETED', { mode, durationMs, ...details });
}

export function auditAnalysisFailed(mode: string, error: string, details?: AuditDetails): void {
  log('ANALYSIS_FAILED', { mode, ...details }, false, error);
}

// ==================== Settings ====================

export function auditSettingsChanged(section: string, details?: AuditDetails): void {
  log('SETTINGS_CHANGED', { section, ...details });
}

export function auditIntegrationConfigured(platform: string, details?: AuditDetails): void {
  log('INTEGRATION_CONFIGURED', { platform, ...details });
}

export function auditIntegrationRemoved(platform: string): void {
  log('INTEGRATION_REMOVED', { platform });
}

// ==================== Presets ====================

export function auditPresetCreated(presetName: string, details?: AuditDetails): void {
  log('PRESET_CREATED', { presetName, ...details });
}

export function auditPresetUpdated(presetName: string, details?: AuditDetails): void {
  log('PRESET_UPDATED', { presetName, ...details });
}

export function auditPresetDeleted(presetName: string): void {
  log('PRESET_DELETED', { presetName });
}

export function auditPresetExported(count: number): void {
  log('PRESETS_EXPORTED', { count });
}

export function auditPresetImported(imported: number, skipped: number): void {
  log('PRESETS_IMPORTED', { imported, skipped });
}

// ==================== Snapshots ====================

export function auditSnapshotSaved(mode: string, snapshotId: string, details?: AuditDetails): void {
  log('SNAPSHOT_SAVED', { mode, snapshotId, ...details });
}

export function auditSnapshotOpened(snapshotId: string, mode: string): void {
  log('SNAPSHOT_OPENED', { snapshotId, mode });
}

export function auditSnapshotDeleted(snapshotId: string): void {
  log('SNAPSHOT_DELETED', { snapshotId });
}

// ==================== Reports ====================

export function auditReportGenerated(reportType: string, details?: AuditDetails): void {
  log('REPORT_GENERATED', { reportType, ...details });
}

export function auditReportDownloaded(reportId: string): void {
  log('REPORT_DOWNLOADED', { reportId });
}

// ==================== Repo Selection ====================

export function auditRepoSelected(repoName: string, repoType: string): void {
  log('REPO_SELECTED', { repoName, repoType });
}

export function auditRepoDeselected(repoName: string): void {
  log('REPO_DESELECTED', { repoName });
}

// ==================== Notifications ====================

export function auditNotificationPermissionChanged(permission: string): void {
  log('NOTIFICATION_PERMISSION_CHANGED', { permission });
}

// ==================== Data Management ====================

export function auditHistoryCleared(): void {
  log('HISTORY_CLEARED', {});
}

export function auditDataExported(type: string): void {
  log('DATA_EXPORTED', { type });
}

export function auditDataImported(type: string, count: number): void {
  log('DATA_IMPORTED', { type, count });
}
